/**
 * Editing Plugin Styles
 *
 * These styles are injected into the document when the EditingPlugin is loaded.
 * CSS custom properties use fallbacks to core grid variables for consistency.
 * Uses CSS nesting for scoping to tbw-grid elements.
 */

@layer tbw-plugins {
  /* ============================================================================
     CSS Custom Properties for Editing
     ============================================================================
     These are defined here so they can be overridden by consumers.
     They reference core grid variables with sensible defaults.
     ============================================================================ */
  tbw-grid {
    /* Editing cell/row backgrounds */
    --tbw-editing-bg: var(--tbw-color-selection);
    --tbw-editing-row-bg: var(--tbw-editing-bg);

    /* Editing input styling */
    --tbw-editing-border: var(--tbw-border-input, 1px solid var(--tbw-color-border-strong));
    --tbw-padding-editing-input: var(--tbw-cell-padding-input, 2px 6px);
    --tbw-font-size-editor: inherit;

    /* Row outline when editing */
    --tbw-editing-row-outline-color: var(--tbw-color-accent);
    --tbw-editing-row-outline-width: 1px;

    /* ============================================================================
     Row Editing State
     ============================================================================ */

    /* Highlight entire row when any cell is being edited (only in row mode, not grid mode) */
    &:not(.tbw-grid-mode) .data-grid-row:has(.editing) {
      background: var(--tbw-editing-row-bg);
      outline: var(--tbw-editing-row-outline-width) solid var(--tbw-editing-row-outline-color);
      outline-offset: calc(-1 * var(--tbw-editing-row-outline-width));
    }

    /* ============================================================================
     Cell Editing State
     ============================================================================ */

    /* Cell in edit mode */
    .data-grid-row > .cell.editing {
      overflow: hidden;
      padding: 0;
      /* Override block to flex for editing inputs */
      display: flex;
      /* Match the non-editing cell height exactly. Non-editing height is:
         max(--tbw-row-height [border-box], line-height + padding + border [content-driven])
         Since editing cells have padding:0, min-height must compensate for both cases:
         - Content-driven: line-height (1lh) + original vertical padding + border
         - Min-height-driven: just var(--tbw-row-height) (border-box already includes padding+border) */
      min-height: max(
        var(--tbw-row-height),
        calc(1lh + 2 * var(--tbw-cell-padding-v, 0.25em) + var(--tbw-border-width, 1px))
      );
      align-items: center;
      justify-content: center;
    }

    /* Standard form inputs inside editing cells */
    .data-grid-row > .cell.editing input:not([type='checkbox']),
    .data-grid-row > .cell.editing select,
    .data-grid-row > .cell.editing textarea {
      width: 100%;
      height: 100%;
      flex: 1 1 auto;
      align-self: stretch;
      min-width: 0;
      border: var(--tbw-editing-border);
      padding: var(--tbw-padding-editing-input);
      font-size: var(--tbw-font-size-editor);
    }

    /* ============================================================================
     Editor Host
     ============================================================================ */

    /* Editor host wrapper uses display:contents to let editor fill cell */
    .tbw-editor-host {
      display: contents;
    }

    /* ============================================================================
     Changed Row Indicator
     ============================================================================ */

    /* Visual indicator for rows that have been modified */
    .data-grid-row.changed {
      /* Could add a left border or background tint here */
    }

    /* ============================================================================
     Invalid Cell Indicator
     ============================================================================ */

    /* Default invalid cell color - can be overridden by consumers */
    --tbw-invalid-bg: light-dark(#fef2f2, #450a0a);
    --tbw-invalid-border-color: light-dark(#ef4444, #f87171);

    /* Visual indicator for cells marked as invalid */
    .data-grid-row > .cell[data-invalid='true'] {
      background: var(--tbw-invalid-bg);
      outline: 1px solid var(--tbw-invalid-border-color);
      outline-offset: -1px;
    }
  }
} /* End @layer tbw-plugins */
